import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface GameSearchResult {
  igdbId: number;
  title: string;
  description?: string;
  releaseDate?: string;
  developer?: string;
  publisher?: string;
  coverUrl?: string;
  platforms?: string[];
  collectionStatus?: {
    inCollection: boolean;
    userGame?: {
      id: string;
      status: 'backlog' | 'wishlist' | 'completed' | 'playing';
    };
  };
}

interface SearchState {
  query: string;
  results: GameSearchResult[];
  total: number;
  isLoading: boolean;
  error: string | null;
  hasSearched: boolean;
  recentSearches: string[];
}

const initialState: SearchState = {
  query: '',
  results: [],
  total: 0,
  isLoading: false,
  error: null,
  hasSearched: false,
  recentSearches: [],
};

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setQuery: (state, action: PayloadAction<string>) => {
      state.query = action.payload;
    },
    setResults: (
      state,
      action: PayloadAction<{ results: GameSearchResult[]; total: number }>,
    ) => {
      state.results = action.payload.results;
      state.total = action.payload.total;
      state.hasSearched = true;
    },
    updateGameCollectionStatus: (
      state,
      action: PayloadAction<{ igdbId: number; inCollection: boolean }>,
    ) => {
      const game = state.results.find(g => g.igdbId === action.payload.igdbId);
      if (game) {
        game.collectionStatus = { inCollection: action.payload.inCollection };
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    clearError: state => {
      state.error = null;
    },
    addRecentSearch: (state, action: PayloadAction<string>) => {
      const query = action.payload.trim();
      if (query && !state.recentSearches.includes(query)) {
        state.recentSearches.unshift(query);
        if (state.recentSearches.length > 10) {
          state.recentSearches.pop();
        }
      }
    },
    clearRecentSearches: state => {
      state.recentSearches = [];
    },
    clearSearch: state => {
      state.query = '';
      state.results = [];
      state.hasSearched = false;
      state.error = null;
    },
  },
});

export const {
  setQuery,
  setResults,
  updateGameCollectionStatus,
  setLoading,
  setError,
  clearError,
  addRecentSearch,
  clearRecentSearches,
  clearSearch,
} = searchSlice.actions;

export default searchSlice.reducer;
